/**
 * @author Muhammad Fahad
 * @datetime April, 14th 2022
 * @description: This service is an intermediate layer between redux store and actual application.
 *                this service provides an easy interface to communicate with redux store.
 */
import { Injectable } from '@angular/core';
import { NgRedux } from '@angular-redux/store';
import * as actions from '../state-management/actionTypes';
import { ISharedData } from '../state-management/state-interfaces';
import {
  updateUser,
  updateUserObject,
  updateShowHeader,
  updateSharedData,
  logoutAction,
  updateSites,
  updateBuildings,
  updateFloors,
  updateUserBrand,
  updateUsersList,
  updateUserLoggedIn,
  updateDeviceList,
  updatePartnerDeviceList,
  updateUnitOrAccessPointUsersList,
  updateAccessPointAcvities,
  updateBuildingActivities,
  prependBuildingActivities,
  prependAccessPointActivities,
  prependUnitActivities,
  updateUnitAcvities,
  updateDashboard,
  updateDashboardUsersList,
  updateActiveIssues,
  prependUsersList,
  prependUsersTabList,
  updateUsersTabList,
  prependUnitFilterAcvities,
  updateUnitFilterAcvities,
  updateRememberMe,
  updateUserLanguage
} from '../state-management/actions';
import { Site } from 'src/app/site-management/site-management-interfaces';
import { Building } from 'src/app/building-managment/building-management-interfaces';
import {
  Floor,
  IFloor,
  IUnit,
  Units,
} from 'src/app/floor-management/floor-management-interface';
import { Brand, Language } from '../models/types';
import {
  IUser,
  SharedUser,
} from 'src/app/user-management/user-management-interfaces';
import { IDevice } from 'src/app/building-managment/building-unit-details/add-lock/lock-management-interfaces';
import { Activities } from 'src/app/shared-layout/Components/activity/activity.interface';
import {
  ActiveIssues,
  UserCards,
} from 'src/app/shared-layout/Components/activity/user-card.interface';

/**
 * This is a general class for getting and setting user object from redux.
 */
@Injectable()
export class StateManagementService {
  /**
   * @description here redux store instance will be injected.
   * @param { NgRedux } ngRedux
   */
  constructor(private ngRedux: NgRedux<any>) {}

  /**
   * These are getter methods for objects from redux.
   */

  /**
   * @description this method will return user details from redux state
   * @returns { IUser }
   */
  getUser(): IUser {
    return this.ngRedux.getState().user;
  }
  /**
   * @description this method will return user details from redux state
   * @returns {  }
   */
  getRememberMe(): any {
    return this.ngRedux.getState().rememberMe;
  }

  updateRememberMe(rememberMe: {
    state: boolean;
    value: string;
    type: string;
  }): void {
    this.ngRedux.dispatch(updateRememberMe(rememberMe));
  }

  /**
   * @description this method will return user details from redux state
   * @returns { IUser }
   */
  getUsersList(): IUser[] {
    return this.ngRedux.getState().usersList;
  }

  /**
   * @description this method will return user details from redux state
   * @returns { IUser }
   */
  getUnitOrAccessPointUsersList(): IUser[] {
    return this.ngRedux.getState().unitOrAccessPointUserList;
  }

  /**
   * @description this method will return userObject from redux state
   * @returns { any }
   */
  getUserObject(): any {
    return this.ngRedux.getState().userObject;
  }

  /**
   * @description this method will return user brand from redux state
   * @returns { string }
   */
  getUserBrand(): Brand {
    return this.ngRedux.getState().userBrand;
  }

  /**
   * @description this method will return user language from redux state
   * @returns { string }
   */
    getUserLanguage(): Language {
      return this.ngRedux.getState().preferredLanguage;
    }

  /**
   * @description this method will return header state from redux state
   * @returns { boolean }
   */
  getShowHeader(): boolean {
    return this.ngRedux.getState().showHeader;
  }

  /**
   * @description this method will return shared data from redux state
   * @returns { ISharedData }
   */
  getSharedData(): ISharedData {
    return this.ngRedux.getState().sharedData;
  }

  /**
   * @description this method will return sites from App Data in redux store
   * @returns { Site }
   */
  getSites(): Site {
    return this.ngRedux.getState()?.appData?.sites;
  }

  /**
   * @description this method will return Building from App Data in redux store
   * @returns { Building }
   */
  getBuildings(): Building {
    return this.ngRedux.getState()?.appData?.buildings;
  }

  /**
   * @description this method will return Floor from App Data in redux store
   * @returns { Floor }
   */
  getFloors(): Floor {
    return this.ngRedux.getState()?.appData?.floors;
  }

  /**
   * @description this method will return sites from App Data in redux store
   * @returns { Site }
   */
  getUnits(floorId: string): Units {
    const floors = this.getFloors();
    const floor: IFloor | undefined = floors?.find((floor: IFloor) => {
      return floorId === floor?.floorId;
    });
    return floor?.unitList;
  }

  /**
   * @description this method will return loggedin status  from redux store
   * @returns { boolean }
   */
  getLoggedIn(): boolean {
    return this.ngRedux.getState()?.isLoggedIn;
  }
  /**
   * These are setter methods for objects from redux.
   */

  /**
   * @description this method will return DeviceList details from redux state
   * @returns { IDevice }
   */
  getDeviceList(): IDevice[] {
    return this.ngRedux.getState().deviceList;
  }

  /**
   * @description this method will return DeviceList details from redux state
   * @returns { IDevice }
   */
  getParttnerDeviceList(): IDevice[] {
    return this.ngRedux.getState().partnerDeviceList;
  }

  /**
   * @description this method will update user details in redux state
   * @param { IUser } user user details object with updated values
   * @returns { void }
   */
  updateUser(user: IUser): void {
    this.ngRedux.dispatch(updateUser(user));
  }

  /**
   * @description this method will update loggedin status in redux state
   * @param { boolean } isLoggedIn loggedin user status update
   * @returns { void }
   */
  updateLoggedIn(isLoggedIn: boolean): void {
    this.ngRedux.dispatch(updateUserLoggedIn(isLoggedIn));
  }

  /**
   * @description this method will update user object in redux state
   * @param { Object } userObject this object will contain user role and accessibility matrix
   * @returns { void }
   */
  updateUserObject(userObject: Object): void {
    this.ngRedux.dispatch(updateUserObject(userObject));
  }

  /**
   * @description this method will update user details in redux state
   * @param { IUser[] } usersList list of all users
   * @returns { void }
   */
  updateUsersList(usersList: IUser[]): void {
    this.ngRedux.dispatch(updateUsersList(usersList));
  }

  /**
   * @description this method will update user details in redux state
   * @param { IUser[] } usersTabList list of all users
   * @returns { void }
   */
  updateUsersTabList(usersTabList: IUser[]): void {
    this.ngRedux.dispatch(updateUsersTabList(usersTabList));
  }
  /**
   * @description this method will update dashboard user details in redux state
   * @param { IUser[] } dashboardusersList list of all  dashboard users
   * @returns { void }
   */
  updateDashboardUsersList(dashboardUsersList: SharedUser): void {
    this.ngRedux.dispatch(updateDashboardUsersList(dashboardUsersList));
  }
  /**
   * @description this method will update user details in redux state
   * @param { IUser[] } usersList list of all users
   * @returns { void }
   */
  updateUnitOrAccessPointUsersList(usersList: IUser[]): void {
    this.ngRedux.dispatch(updateUnitOrAccessPointUsersList(usersList));
  }
  /**
   * @description this method will update header state in redux state
   * @param { boolean } headerState boolean to track header hide/show. Default true
   * @returns { void }
   */
  updateShowHeader(headerState: boolean = true): void {
    this.ngRedux.dispatch(updateShowHeader(headerState));
  }

  /**
   * @description this method will update user brand in redux state
   * @param { Brand } brand the brand that user has selected
   * @returns { void }
   */
  updateUserBrand(brand: Brand): void {
    this.ngRedux.dispatch(updateUserBrand(brand));
  }
  /**
   * @description this method will update user language in redux state
   * @param { Language } Language the Language that user has selected
   * @returns { void }
   */
  updateUserLanguage(language: Language): void {
    this.ngRedux.dispatch(updateUserLanguage(language));
  }
  /**
   * @description this method will update device details in redux state
   * @param { IUser[] } deviceList list of all device
   * @returns { void }
   */
  updateDeviceList(deviceList: IDevice[]): void {
    this.ngRedux.dispatch(updateDeviceList(deviceList));
  }

  /**
   * @description this method will update device details in redux state
   * @param { IUser[] } deviceList list of all device
   * @returns { void }
   */
  updatePartnerDeviceList(deviceList: IDevice[]): void {
    this.ngRedux.dispatch(updatePartnerDeviceList(deviceList));
  }

  /**
   * @description This method must be called after calling user profile api to set user object in redux.
   *              this method transform user data to an storable form
   * @param { any } rawUser user profile raw data
   * @returns { void}
   */
  parseUserObjectAndDispatch(rawUser: any): IUser {
    let user: IUser = {
      userId: '',
      email: '',
      phoneNumber: '',
      name: '',
      userType: '',
      invationPending: false,
      userImgName: '',
      userImgKey: '',
      userImgUrl: '',
    };
    this.updateUser(user);

    this.ngRedux.dispatch({
      type: actions.UPDATE_USER_BRAND,
      brand: rawUser['brandname'],
    });
    return user;
  }

  /**
   * @description this method will update shared data in redux state
   * @param { ISharedData } sharedData
   * @returns { void }
   */
  updateSharedData(sharedData: ISharedData): void {
    this.ngRedux.dispatch(updateSharedData(sharedData));
  }

  /**
   * @description this method will set sites in App Data in redux store
   * @param { Site } sites the list of sites to store in redux store
   * @returns { void }
   */
  updateSites(sites: Site): void {
    this.ngRedux.dispatch(updateSites(sites));
  }

  /**
   * @description this method will set sites in App Data in redux store
   * @param { Buildind } buildings the list of buildings to store in redux store
   * @returns { void }
   */
  updateBuildings(buildings: Building): void {
    this.ngRedux.dispatch(updateBuildings(buildings));
  }

  /**
   * @description this method will set Floor in App Data in redux store
   * @param { Floor } floors the list of floors to store in redux store
   * @returns { void }
   */
  updateFloors(floors: Floor): void {
    // sorting before update floor list
    floors?.forEach((floor: IFloor, index: number) => {
      if (floor.unitList) {
        const accessPoints = floor?.unitList
          ?.filter((unitData: IUnit) =>
            unitData.hasOwnProperty('accessPointId')
          )
          ?.sort((accessPointA: IUnit, accessPointB: IUnit) =>
            accessPointA.accessPointName!.localeCompare(
              accessPointB.accessPointName!,
              'en',
              { numeric: true }
            )
          );

        const units = floor?.unitList
          ?.filter((accessPointData: IUnit) =>
            accessPointData.hasOwnProperty('unitId')
          )
          ?.sort((unitA: IUnit, unitB: IUnit) =>
            unitA.unitName.localeCompare(unitB.unitName, 'en', {
              numeric: true,
            })
          );
        floor.unitList = [...units, ...accessPoints];
      }
    });

    this.ngRedux.dispatch(updateFloors(floors));
  }

  /**
   * @description this method will set sites in App Data in redux store
   * @param { Buildind } buildings the list of buildings to store in redux store
   * @returns { void }
   */
  updateUnits(floorId: string, units: Units): void {
    const floors: Floor | undefined = this.getFloors();
    floors?.forEach((floor: IFloor) => {
      if (floor?.floorId === floorId) {
        floor.unitList = units;
      }
    });
    this.updateFloors(floors);
  }

  /**
   * These are remove methods for objects in redux.
   */

  /**
   * @description this method will remove user details from redux and places null
   * @returns { void }
   */
  removeUser(): void {
    this.ngRedux.dispatch({ type: actions.UPDATE_USER, userObject: null });
  }

  /**
   * @description this method will remove user object from redux and places null
   * @returns { void }
   */
  removeUserObject(): void {
    this.ngRedux.dispatch({
      type: actions.UPDATE_USER_OBJECT,
      userObject: null,
    });
  }

  /**
   * @description this method will remove user phone number from user details state
   * @returns { void }
   */
  removeUserPhoneNumber(): void {
    const user = this.ngRedux.getState().userObject;
    user.phoneNumber = null;
    this.ngRedux.dispatch({
      type: actions.UPDATE_USER_OBJECT,
      userObject: user,
    });
  }

  /**
   * @description this method will reset all redux state
   * @returns { void }
   */
  logout(): void {
    this.ngRedux.dispatch(logoutAction());
  }

  /**
   * @description This is for returning subscription for redux.
   *  @param listener the listener method that will be called every time this subscription runs
   * @returns { void }
   */
  subscribe(listener: any): any {
    return this.ngRedux.subscribe(listener);
  }

  updateLastActivity() {
    this.ngRedux.dispatch({
      type: actions.LAST_ACTIVITY_TIME,
      time: Date.now(),
    });
  }

  /**
   * @description This function is to get last activity time.
   * @returns { number }
   */
  getLastActivityTime() {
    return this.ngRedux.getState().time;
  }

  /**
   * @description this method will return DeviceList details from redux state
   * @returns { accessPointActivities }
   */
  getAccessPointAcvities(): any[] {
    return this.ngRedux.getState().accessPointActivities;
  }

  /**
   * @description this method will update device details in redux state
   * @param { IUser[] } deviceList list of all device
   * @returns { void }
   */
  updateAccessPointAcvities(accessPointActivities: Activities): void {
    this.ngRedux.dispatch(updateAccessPointAcvities(accessPointActivities));
  }

  /**
   * @description this method will update building activity details in redux state
   * @param { Activities } buildingActivityList list of all building activities
   * @returns { void }
   */
  updateBuildingAcvities(buildingActivityList: Activities): void {
    this.ngRedux.dispatch(updateBuildingActivities(buildingActivityList));
  }
  /**
   * @description this method will prepend building activity details in redux state
   * @param { Activities } buildingActivityList list of all building activities
   * @returns { void }
   */
  prependBuildingAcvities(buildingActivityList: Activities): void {
    this.ngRedux.dispatch(prependBuildingActivities(buildingActivityList));
  }
  /**
   * @description this method will prepend usersList in redux state
   * @param { IUser[] } usersList list of all users
   * @returns { void }
   */
  prependUsersList(usersList: IUser[]): void {
    this.ngRedux.dispatch(prependUsersList(usersList));
  }

  /**
   * @description this method will prepend usersList of users tab in redux state
   * @param { IUser[] } usersTabList list of all users
   * @returns { void }
   */
  prependUsersTabList(usersTabList: IUser[]): void {
    this.ngRedux.dispatch(prependUsersTabList(usersTabList));
  }

  /**
   * @description this method will prepend building activity details in redux state
   * @param { Activities } accessPointActivities list of all building activities
   * @returns { void }
   */
  prependAccesPointAcvities(accessPointActivities: Activities): void {
    this.ngRedux.dispatch(prependAccessPointActivities(accessPointActivities));
  }

  /**
   * @description this method will prepend unit activity details in redux state
   * @param { Activities } unitActivities list of all unit activities
   * @returns { void }
   */
  prependUnitAcvities(unitActivities: Activities): void {
    this.ngRedux.dispatch(prependUnitActivities(unitActivities));
  }

  /**
   * @description this method will prepend unit activity details in redux state
   * @param { Activities } unitActivities list of all unit activities
   * @returns { void }
   */
  prependUnitFilterAcvities(unitActivities: Activities): void {
    this.ngRedux.dispatch(prependUnitFilterAcvities(unitActivities));
  }

  /**
   * @description this method will update device details in redux state
   * @param { Activities } activityList list of all activity
   * @returns { void }
   */
  updateUnitAcvities(unitActivities: Activities): void {
    this.ngRedux.dispatch(updateUnitAcvities(unitActivities));
  }

  /**
   * @description this method will update device details in redux state
   * @param { Activities } activityList list of all activity
   * @returns { void }
   */
  updateUnitFilterAcvities(unitActivities: Activities): void {
    this.ngRedux.dispatch(updateUnitFilterAcvities(unitActivities));
  }

  /**
   * @description this method will update device details in redux state
   * @param { Activities } activityList list of all activity
   * @returns { void }
   */
  updateDashboard(dashboardActivity: UserCards): void {
    this.ngRedux.dispatch(updateDashboard(dashboardActivity));
  }

  /**
   * @description this method will update active Issues details in redux state
   * @param { Activities } activityList list of all activity
   * @returns { void }
   */
  updateActiveIssues(activeIssuesActivity: ActiveIssues): void {
    this.ngRedux.dispatch(updateActiveIssues(activeIssuesActivity));
  }
}
